import React, { forwardRef, type RefObject, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { DrawIoEmbed, type DrawIoEmbedRef } from "react-drawio";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import ExportModal from "~/components/Document/exportModal";
import { CommonButton } from "~/components/ui/commonButton";
import { TOAST_MESSAGES } from "~/constants/messages";
import { ProcessLockApi } from "~/functions/api/processLock";
import { useNavigationPrompt } from "~/functions/navigationHooks";
import CheckWhite from "~/images/check_white.svg?react";
// DOCUMENT_TYPES intentionally not used - keeping import for future use
import EditWhite from "~/images/edit_white.svg?react";
import ExportPrimary from "~/images/export_primary.svg?react";
import type { RootState } from "~/store";
import styles from "./index.module.scss";

interface DrawioProps {
  xml: string;
  documentType: string;
  documentName: string;
  onSave?: (xml: string) => Promise<void>;
}

// DrawioContainerのref型
export type DrawioContainerRef = {
  handleExport: () => void;
  handleSave: () => void;
  getCurrentXml: () => string;
};

export const DrawioContainer = forwardRef<DrawioContainerRef, DrawioProps>(
  ({ xml, documentType, documentName, onSave }, ref) => {
    const [isEditable, setIsEditable] = useState(false);
    const [currentXml, setCurrentXml] = useState(xml || "");
    const [isLoading, setIsLoading] = useState(true);
    const drawioRef = useRef<DrawIoEmbedRef>(null);
    const { currentUser, currentProject } = useSelector((state: RootState) => state.auth);
    const [showExportModal, setShowExportModal] = useState(false);

    useEffect(() => {
      if (xml && xml !== currentXml) {
        setCurrentXml(xml);
      }
    }, [xml, currentXml]);

    const handleEdit = async () => {
      try {
        const processLockApi = new ProcessLockApi();
        const response = await processLockApi.lock(currentUser.organization_id, currentProject.id, {
          type: documentType,
          mode: "edit",
        });
        if (response === null) {
          toast.error(TOAST_MESSAGES.DRAWIO.LOCK_FAILED);
          setIsEditable(false);
          return;
        }
        setIsEditable(true);
      } catch (_error) {
        toast.error(TOAST_MESSAGES.DRAWIO.ERROR);
      }
    };

    const handleSave = async () => {
      if (drawioRef.current) {
        drawioRef.current.exportDiagram({ format: "xmlsvg" });
      } else {
        toast.error(TOAST_MESSAGES.DRAWIO.REF_NOT_FOUND);
      }
    };

    const handleCancel = async () => {
      const processLockApi = new ProcessLockApi();
      try {
        const response = await processLockApi.unlock(
          currentUser.organization_id,
          currentProject.id,
          {
            type: documentType,
            mode: "edit",
          },
        );
        if (response !== null) {
          if (xml) {
            setCurrentXml(xml);
          }
        }
      } catch (_error) {
        toast.error(TOAST_MESSAGES.DRAWIO.ERROR);
      } finally {
        setIsEditable(false);
      }
    };

    useNavigationPrompt(
      isEditable,
      "編集内容が保存されていません。ページを離れてもよろしいですか？",
      handleSave,
      handleCancel,
    );

    // onExportハンドラー
    const handleExport = (data: {
      format: string;
      data?: string;
      xml?: string;
      filename?: string;
    }) => {
      if (data.format === "png" || data.format === "jpg") {
        if (data.data) {
          const link = document.createElement("a");
          link.href = data.data;
          // data.filenameがある場合はそれを使用、なければドキュメント名を使用
          const filename = data.filename || documentName;
          link.download = `${filename}.${data.format}`;
          link.click();
          toast.success(TOAST_MESSAGES.DRAWIO.EXPORT_SUCCESS);
        } else {
          toast.error(TOAST_MESSAGES.DRAWIO.EXPORT_DATA_NOT_FOUND);
        }
      } else if (data.format === "svg" || data.format === "xmlsvg") {
        if (data.data && !isEditable) {
          try {
            const link = document.createElement("a");
            link.href = data.data;
            // data.filenameがある場合はそれを使用、なければドキュメント名を使用
            const filename = data.filename || documentName;
            link.download = `${filename}.svg`;
            link.click();
            toast.success(TOAST_MESSAGES.DRAWIO.EXPORT_SUCCESS);
          } catch (_error) {
            toast.error(TOAST_MESSAGES.DRAWIO.SVG_EXPORT_FAILED);
          }
        } else {
          const xmlData = data.data || data.xml || "";
          setCurrentXml(xmlData);
          // 保存処理の場合、親コンポーネントのonSave関数を呼び出す
          if (isEditable && onSave) {
            onSave(xmlData)
              .then(() => {
                setIsEditable(false);
              })
              .catch((error) => {
                toast.error(TOAST_MESSAGES.DRAWIO.SAVE_FAILED);
                console.error(error);
              });
          }
        }
      }
    };

    // エクスポートボタンがクリックされたときの処理
    const handleExportButtonClick = () => {
      setShowExportModal(true);
    };

    const getCurrentXml = (): string => {
      return currentXml;
    };

    React.useImperativeHandle(ref, () => ({
      handleExport: handleExportButtonClick,
      handleSave,
      getCurrentXml,
    }));

    // エクスポートモーダルをレンダリングする
    const renderExportModal = () => {
      if (!showExportModal) return null;

      return ReactDOM.createPortal(
        <ExportModal
          documentType={documentType}
          onClose={() => setShowExportModal(false)}
          exportData={{
            type: "drawio",
            exportData: {
              xml: currentXml,
              documentType: documentType,
              drawioRef: drawioRef as RefObject<DrawIoEmbedRef>,
            },
            name: documentName,
          }}
        />,
        document.body,
      );
    };

    // ユーザー権限がない場合や履歴表示モードの場合はボタンを表示しない
    // また、Draw.ioのロード中もボタンを非表示にする
    const showEditButtons = currentUser && currentProject && onSave && !isLoading;

    return (
      <div>
        {showEditButtons && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              marginBottom: "12px",
            }}
          >
            {isEditable ? (
              <>
                <CommonButton variant="label" onClick={handleCancel}>
                  キャンセル
                </CommonButton>
                <CommonButton icon={<CheckWhite />} onClick={handleSave}>
                  保存
                </CommonButton>
              </>
            ) : (
              <>
                <CommonButton icon={<EditWhite />} onClick={handleEdit}>
                  編集
                </CommonButton>
                <CommonButton
                  icon={<ExportPrimary />}
                  variant="icon"
                  onClick={handleExportButtonClick}
                  disabled={isEditable}
                />
              </>
            )}
          </div>
        )}
        <div className={styles["drawio"]}>
          <DrawIoEmbed
            configuration={{
              defaultFonts: ["Helvetica"],
              css: `.mxPopupMenu tbody tr:nth-child(3) { display: none !important; } .mxPopupMenu tbody tr:nth-child(2) { display: none !important; } div[style*="position: fixed"][style*="bottom: 20px"][style*="border-radius: 16px"] { 
      display: none !important; 
    }
                        `,
            }}
            ref={drawioRef}
            key={isEditable ? "editable" : "readonly"}
            xml={currentXml}
            urlParameters={{
              ui: "simple",
              libraries: true,
              saveAndExit: false,
              noExitBtn: true,
              lang: "ja",
              chrome: isEditable,
              noSaveBtn: true,
              ...(isEditable && { edit: "_blank" }),
            }}
            onExport={handleExport}
            onLoad={() => {
              setIsLoading(false);
            }}
          />
        </div>
        {renderExportModal()}
      </div>
    );
  },
);
